import React, { useState } from 'react';
import { Send, Heart, Clock, Shield, Users, Check, MapPin } from 'lucide-react';

const PrayerPage = () => {
  const [prayerRequest, setPrayerRequest] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API}/prayer-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name || null,
          email: email || null,
          request_text: prayerRequest,
          is_urgent: isUrgent
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit prayer request');
      }

      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setPrayerRequest('');
        setName('');
        setEmail('');
        setIsUrgent(false);
      }, 4000);
    } catch (err) {
      setError('Unable to submit prayer request. Please try again.');
      console.error('Prayer request error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const prayerCategories = [
    { icon: Heart, title: 'Health & Healing', description: 'Physical, mental, and emotional healing needs' },
    { icon: Users, title: 'Family & Relationships', description: 'Marriage, parenting, and relationship restoration' },
    { icon: Shield, title: 'Protection & Safety', description: 'Spiritual warfare and divine protection' },
    { icon: Clock, title: 'Guidance & Wisdom', description: 'Life decisions and spiritual direction' }
  ];

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 lg:mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 font-display">
              Prayer Support
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-6 font-sans">
              You are never alone in your struggles. Our dedicated prayer team stands ready 
              to intercede for you, offering confidential support and spiritual encouragement.
            </p>
            
            {/* Prayer Meeting Highlight */}
            <div className="max-w-2xl mx-auto bg-gradient-to-r from-[#f17b21] to-orange-600 rounded-xl p-6 text-white">
              <h3 className="text-xl font-bold mb-2">Join Us for Corporate Prayer</h3>
              <p className="mb-3">Experience the power of praying together in person</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
                <div className="bg-white/20 rounded-lg p-3">
                  <p className="font-semibold">Wednesday Prayer Meeting</p>
                  <p>7:00 PM - 8:00 PM</p>
                </div>
                <div className="bg-white/20 rounded-lg p-3">
                  <p className="font-semibold">Monday Online Devotion</p>
                  <p>7:00 PM (15 minutes)</p>
                </div>
              </div>
              <a 
                href="/visit"
                className="inline-block bg-white text-[#f17b21] hover:bg-orange-50 px-6 py-2 rounded-lg font-semibold transition-colors duration-300"
              >
                Plan Your Visit
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Prayer Request Form */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Form */}
              <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-6 font-display">
                  Submit a Prayer Request
                </h2>
                
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        Your Name (Optional)
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter your name"
                        className="w-full p-3 lg:p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        Email (Optional - for prayer updates)
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        className="w-full p-3 lg:p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent font-sans"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-sans">
                        How can we pray for you? *
                      </label>
                      <textarea
                        value={prayerRequest}
                        onChange={(e) => setPrayerRequest(e.target.value)}
                        placeholder="Share your prayer need here... All requests are kept completely confidential and are prayed over by our dedicated prayer team."
                        rows={6}
                        className="w-full p-3 lg:p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#f17b21] focus:border-transparent resize-none font-sans"
                        required
                      />
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="urgent"
                        checked={isUrgent}
                        onChange={(e) => setIsUrgent(e.target.checked)}
                        className="w-4 h-4 text-[#f17b21] border-gray-300 rounded focus:ring-[#f17b21]"
                      />
                      <label htmlFor="urgent" className="text-sm text-gray-700 font-sans">
                        This is an urgent prayer request
                      </label>
                    </div>
                    
                    <div className="flex items-center space-x-3 text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                      <Shield className="w-5 h-5 text-[#f17b21] flex-shrink-0" />
                      <span className="font-sans">All prayer requests are kept strictly confidential and prayed over by our prayer team</span>
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                        <p className="text-red-600 text-sm font-sans">{error}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#f17b21] hover:bg-[#d86919] text-white px-6 lg:px-8 py-3 lg:py-4 rounded-lg font-semibold transition-colors duration-300 flex items-center justify-center space-x-2 font-sans disabled:opacity-70"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          <span>Send Prayer Request</span>
                        </>
                      )}
                    </button>
                  </form>
                ) : (
                  <div className="text-center py-8 lg:py-12">
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-4 font-display">Prayer Request Received</h3>
                    <p className="text-gray-600 font-sans">
                      Thank you for sharing your heart with us. Our prayer team will be interceding for you, 
                      and if you provided an email, we'll send you updates on how God is working.
                    </p>
                  </div>
                )}
              </div>

              {/* Prayer Ministry Info */}
              <div className="space-y-6 lg:space-y-8">
                <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8">
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-6 font-display">Our Prayer Ministry</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#f17b21] rounded-full flex items-center justify-center flex-shrink-0">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2 font-sans">Confidential & Secure</h4>
                        <p className="text-gray-600 text-sm lg:text-base font-sans">Every prayer request is kept completely confidential and handled with the utmost care and respect.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#f17b21] rounded-full flex items-center justify-center flex-shrink-0">
                        <Heart className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2 font-sans">Dedicated Prayer Team</h4>
                        <p className="text-gray-600 text-sm lg:text-base font-sans">Our committed prayer warriors lift up every request with faith and persistence before the throne of grace.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#f17b21] rounded-full flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2 font-sans">Faithful Prayer</h4>
                        <p className="text-gray-600 text-sm lg:text-base font-sans">All prayer requests are faithfully prayed over by our dedicated prayer team.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Prayer Categories */}
                <div className="bg-gray-50 rounded-2xl p-6 lg:p-8">
                  <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-6 font-display">We Pray For</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {prayerCategories.map((category, index) => {
                      const IconComponent = category.icon;
                      return (
                        <div key={index} className="flex items-center space-x-4 p-4 bg-white rounded-lg">
                          <div className="w-10 h-10 bg-[#f17b21] rounded-full flex items-center justify-center">
                            <IconComponent className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 font-sans text-sm lg:text-base">{category.title}</h4>
                            <p className="text-gray-600 text-xs lg:text-sm font-sans">{category.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Church Location Prayer */}
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 lg:p-8" data-testid="location-prayer-request">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-lg lg:text-xl font-bold text-gray-800 mb-2 font-display">
                        Pray for Our Permanent Home
                      </h3>
                      <p className="text-gray-700 text-sm lg:text-base font-sans leading-relaxed mb-3">
                        El Bethel Church is currently meeting at a temporary location. We are seeking God's direction for a permanent place of worship in the Grand Prairie / Dallas area. Please join us in prayer that the Lord would provide the right space for our growing church family.
                      </p>
                      <p className="text-amber-700 text-sm font-sans font-medium italic">
                        "Unless the LORD builds the house, the builders labor in vain." — Psalm 127:1
                      </p>
                    </div>
                  </div>
                </div>

                {/* Join Prayer Team */}
                <div className="bg-gradient-to-r from-[#f17b21] to-[#ff9649] rounded-2xl p-6 lg:p-8 text-white">
                  <h3 className="text-xl lg:text-2xl font-bold mb-4 font-display">Join Our Prayer Team</h3>
                  <p className="mb-6 opacity-90 font-sans text-sm lg:text-base">
                    Feel called to intercede for others? Join our dedicated team of prayer warriors 
                    who commit to praying for requests from our community.
                  </p>
                  <button 
                    onClick={() => window.location.href = 'mailto:contact@elbethel.online?subject=Prayer Team Interest'}
                    className="bg-white text-[#f17b21] hover:bg-gray-100 px-6 py-3 rounded-lg font-semibold transition-colors duration-300 font-sans"
                  >
                    Join Prayer Team
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrayerPage;